import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Select, Switch, Space, Tag, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { photoTypeApi } from '../../services/api';

const AdminPhotoTypes = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async (page = 1) => {
    setLoading(true);
    try {
      const res = await photoTypeApi.list({ page, limit: pagination.limit });
      setData(res.data.list || []);
      setPagination(prev => ({ ...prev, page, total: res.data.pagination?.total || 0 }));
    } catch (err) {
      message.error('加载照片类型失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleAdd = () => {
    setEditingItem(null);
    form.resetFields();
    form.setFieldsValue({ level: 'batch', sortOrder: 0, status: 1 });
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingItem(record);
    form.setFieldsValue({
      name: record.name,
      level: record.level,
      sortOrder: record.sort_order,
      status: record.status,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        await photoTypeApi.update(editingItem.id, {
          name: values.name,
          level: values.level,
          sortOrder: values.sortOrder,
          status: values.status ? 1 : 0,
        });
        message.success('更新成功');
      } else {
        await photoTypeApi.create({
          name: values.name,
          level: values.level,
          sortOrder: values.sortOrder,
          status: values.status ? 1 : 0,
        });
        message.success('创建成功');
      }
      setModalOpen(false);
      fetchData();
    } catch (err) {
      if (err.response?.data?.message) {
        message.error(err.response.data.message);
      }
    }
  };

  const handleDelete = async (id) => {
    try {
      await photoTypeApi.delete(id);
      message.success('删除成功');
      fetchData();
    } catch (err) {
      message.error(err.response?.data?.message || '删除失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name' },
    {
      title: '层级',
      dataIndex: 'level',
      render: (level) => (
        <Tag color={level === 'product' ? 'green' : 'blue'}>
          {level === 'product' ? '货品级' : '批次级'}
        </Tag>
      ),
    },
    { title: '排序', dataIndex: 'sort_order', width: 80 },
    {
      title: '状态',
      dataIndex: 'status',
      render: (status) => <Tag color={status === 1 ? 'green' : 'red'}>{status === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '操作',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm title="确定删除此照片类型？" onConfirm={() => handleDelete(record.id)} okText="确定" cancelText="取消">
            <Button type="link" danger icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0 }}>照片类型管理</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>新增照片类型</Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          current: pagination.page,
          total: pagination.total,
          pageSize: pagination.limit,
          onChange: (page) => fetchData(page),
          showTotal: (total) => `共 ${total} 条`,
        }}
      />

      <Modal
        title={editingItem ? '编辑照片类型' : '新增照片类型'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="如：原石照片" maxLength={50} />
          </Form.Item>
          <Form.Item name="level" label="层级" rules={[{ required: true }]}>
            <Select options={[
              { label: '批次级（关联到批次）', value: 'batch' },
              { label: '货品级（关联到货品）', value: 'product' },
            ]} />
          </Form.Item>
          <Form.Item name="sortOrder" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="status" label="启用" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="禁用" defaultChecked />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPhotoTypes;
